<template>
    <div class="chip-field" @click="handleBlur">
        <label for="chips" class="font-weight-bold">{{ selectQuoteText }}</label>
        <div ref="inputContainer" @click.stop class="chip-container clickable-chip" id="chips">
            <div
                    v-for="(pill, index) in pills"
                    :key="index"
                    v-show="editingIndex !== index"
                    @click.stop="editPill(index)"
                    class="chip clickable-chip"
            >
                <span class="pill-text clickable-chip">{{ pill }}</span>
                <v-icon size="18" class="ml-2 clickable-chip" color="primary">mdi-pencil</v-icon>
            </div>
            <div v-if="editingIndex !== null" class="input-wrapper">
                <input
                        v-model="newValue"
                        placeholder=""
                        @keydown.tab="handleBlur"
                        @keydown.enter="confirmValue"
                        class="chip-input pl-1"
                        ref="chipInput"
                />
                <span
                        v-if="newValue && isValueAllowed && !isDuplicate"
                        @click.stop="confirmValue"
                        class="confirm-text confirm-text--primary"
                >

                    {{ confirmText }}
                </span>
                <span v-else-if="editingIndex !== null"
                      class="confirm-text confirm-text--neutral"
                >
                    {{ confirmText }}

                </span>
            </div>
        </div>
        <div v-if="newValue && isDuplicate && editingIndex !== null" class="error-text">
            {{ duplicateText }}
        </div>
        <div v-if="newValue && !isValueAllowed && !isDuplicate && editingIndex !== null" class="error-text">
            {{ notAvailableText }}
        </div>
        <div v-if="newValue && !isValueAllowed && !isDuplicate && editingIndex !== null" class="next-text">
            {{ closestAvailableText }}
        </div>
        <div v-if="newValue && isValueAllowed && !isDuplicate && editingIndex !== null" class="info-text">
            {{ availableForReserveText }}
        </div>
        <p class="note">{{ noteText }}</p>
    </div>
</template>

<script>
  export default {
    props: {
      allowedValues: { type: Array, default: () => [] },
      value: { type: Array, default: () => [] },
      selectQuoteText: { type: String },
      duplicateTextTemplate: { type: String },
      notAvailableTextTemplate: { type: String },
      closestAvailableTextTemplate: { type: String },
      availableForReserveTextTemplate: { type: String },
      confirmButtonTextTemplate: { type: String },
      noteText: { type: String }
    },
    data () {
      return {
        editingIndex: null,
        newValue: ''
      }
    },
    computed: {
      pills: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        }
      },
      allowedValuesComputed () {
        return this.allowedValues.filter(value => {
          if (this.editingIndex !== null && this.pills[this.editingIndex] === value) {
            return true
          }
          return !this.pills.includes(value)
        })
      },
      isValueAllowed () {
        return this.allowedValuesComputed.includes(this.newValue)
      },
      isDuplicate () {
        return this.pills.includes(this.newValue) && this.newValue !== this.pills[this.editingIndex]
      },
      closestAvailableValue () {
        if (!this.newValue) return null
        let closest = null
        let smallestDifference = Infinity
        this.allowedValuesComputed.forEach(value => {
          const difference = Math.abs(value - this.newValue)
          if (difference < smallestDifference) {
            smallestDifference = difference
            closest = value
          }
        })
        return closest
      },
      duplicateText () {
        return this.duplicateTextTemplate.replace('{value}', this.newValue)
      },
      notAvailableText () {
        return this.notAvailableTextTemplate.replace('{value}', this.newValue)
      },
      closestAvailableText () {
        return this.closestAvailableTextTemplate.replace('{value}', this.closestAvailableValue)
      },
      availableForReserveText () {
        return this.availableForReserveTextTemplate.replace('{value}', this.newValue)
      },
      confirmText () {
        return this.confirmButtonTextTemplate.replace('{value}', this.newValue)
      }
    },
    watch: {
      editingIndex (newValue) {
        const isEditing = newValue !== null
        this.$emit('editing', isEditing)
      }
    },
    methods: {
      editPill (index) {
        if (this.editingIndex !== null && this.isValueAllowed) {
          this.pills[this.editingIndex] = this.newValue
        }
        this.editingIndex = index
        this.newValue = this.pills[index]
        this.$nextTick(() => {
          this.$refs.chipInput.focus()
        })
      },
      confirmValue () {
        if (this.isValueAllowed && !this.isDuplicate) {
          this.pills[this.editingIndex] = this.newValue
          this.editingIndex = null
          this.newValue = ''
        }
      },
      handleBlur (e) {
        this.editingIndex = null
        this.newValue = ''
      }
    }
  }
</script>
<style>
.chip-field {
display: flex;
flex-direction: column;
max-width: 500px;
margin: auto;
font-family: Arial, sans-serif;
}

label {
margin-bottom: 10px;
}

.chip-container {
display: flex;
align-items: center;
border: 1px solid #ccc;
border-radius: 8px;
padding: 5px;
flex-wrap: wrap;
}

.chip {
display: flex;
align-items: center;
margin: 1px 5px;
cursor: pointer;
padding: 5px 10px;
position: relative;
color: var(--primary);
border-radius: 16px;
}

.chip::before {
content: '';
position: absolute;
border-radius: 16px;
top: 0;
left: 0;
right: 0;
bottom: 0;
background-color: var(--primary);
opacity: 0.12;
z-index: 1;
pointer-events: none;
}

.input-wrapper {
display: flex;
align-items: center;
flex-grow: 1;
position: relative;
min-width: 150px;
margin: 5px;
}

.chip-input {
border: none;
outline: none;
flex-grow: 1;
min-width: 0;
}

.confirm-text {

font-weight: 700;
font-size: 12px;
position: absolute;
right: 10px;
white-space: nowrap;
}

.confirm-text--neutral{
    cursor: default;
    color: var(--neutral);
}
.confirm-text--primary{
    cursor: pointer;
    color: var(--primary);
}
.error-text,.info-text,.next-text {
color: var(--dark);

text-align: center;
font-weight: 700;
font-size: 0.9rem;
}

.error-text{
margin: 18px 0px 9px 0px;
}

.info-text {
margin: 18px 0px 18px 0px;
}

.note {
font-size: 12px;
color: #666;
margin-top: 10px;
}
</style>
